/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-02-28 00:00:00
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import moment from 'moment';
import React, { Component } from 'react';
import {
  Page,
  Layout,
  Card,
  Badge,
  ButtonGroup,
  SkeletonDisplayText,
  TextStyle,
  EmptyState,
  Button
} from '@shopify/polaris';
import { connect } from 'react-redux';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import LoadingBar from 'react-top-loading-bar';
import { DuplicateMinor } from '@shopify/polaris-icons';

import { routes } from 'src/routes';
import history from 'src/routes/history';

import MainMenu from 'src/components/containers/main-menu';
import SelectTemplateModal from 'src/components/containers/shopify-integration/select-template.modal';
import RenameTemplateModal from 'src/components/containers/shopify-integration/manage-template-instance/modal/rename-template.modal';
import ChangeTemplateModal from 'src/components/containers/shopify-integration/manage-template-instance/modal/change-template.modal';
import RemoveTemplateModal from 'src/components/containers/shopify-integration/manage-template-instance/modal/remove-template.modal';
import CustomizeTemplateModal from 'src/components/containers/shopify-integration/manage-template-instance/modal/customize-template.modal';
import TemplateChangedModal from 'src/components/containers/shopify-integration/template-changed.modal';
import PopOverActionManageButton from 'src/components/views/shopify-integration/popover-action-manage-button';
import TitleHelp from 'src/components/views/common/title-help';
import { showSABToast } from 'src/integrations/shopify-app-bridge/toast';
import { getThemeReviewUrl, getShop } from 'src/helpers';
import { trackFSEvent } from 'src/integrations/fullStory';
import { copyClipBoard } from 'src/helpers/shopify-integration-helpers';

import ThumbnailDefaultTemplate from 'src/assets/images/shopify-integration/ThumbnailDefaultTemplate.png';
import ThumbnailBasicTemplate from 'src/assets/images/shopify-integration/ThumbnailBasicTemplate.png';
import EmptyTemplate from 'src/assets/images/shopify-integration/empty-template.png';

import { LIMIT_CUSTOM_TEMPLATE_INSTANCE } from 'src/constants/shopify-integration';
import { URL } from 'src/constants/links';

import * as shopifyIntegrationThemeAction from 'actions/shopify-integration/theme-actions';
import * as templateInstanceAction from 'actions/shopify-integration/template-instance-actions';
import * as themeIntegrationAction from 'actions/shopify-integration/theme-integration-actions';
import { savePrevRoute } from 'src/helpers/session-storage-helper';

type Props = {
  installStatus: string,
  installAppStatus: string,
  themeSetupV2: boolean,
  isTemplateLibrary?: boolean
};

type ComposedProps = Props & WithI18nProps;

type State = {};

class ManageTemplateInstance extends Component<ComposedProps, State> {
  loadingBar = null;
  state = {
    // Current theme and template
    themeId: 0,
    isLiveTheme: false,
    currentTemplateId: '',

    // themeId: '128297599057',
    // isLiveTheme: false,
    // currentTemplateId: '65cfa696-c878-4e9f-a57b-915c1f59a3d8',

    openModal: false,

    // Rename template modal
    openRenameModal: false,
    selectedTemplateId: '',
    selectedTemplateName: '',
    selectedTemplateInstance: {},

    // Change template modal
    openChangeModal: false,

    // Remove template modal
    openRemoveModal: false,

    // Customize template modal
    openCustomizeModal: false,

    openTemplateChangedModal: false
  };

  constructor(props) {
    super(props);
    if (props.isTemplateLibrary) {
      localStorage.removeItem(`${getShop()}-manageTemplate`);
    }
  }

  componentDidMount() {
    const {
      getTemplateInstances,
      getBoostTemplateInstances,
      getAllThemes,
      themes = []
    } = this.props;

    // Get list of store's template instance
    getTemplateInstances();

    // Get Boost template
    getBoostTemplateInstances();

    if (!themes.length) {
      getAllThemes();
    }

    this.checkTheme();

    window.addEventListener('beforeunload', this.cacheCurrentTheme);
  }

  componentWillReceiveProps(nextProps) {
    const {
      getAllThemesLoading: getAllThemesLoadingPrev,
      getTemplateInstancesLoading: getTemplateInstancesLoadingPrev
    } = this.props;
    const {
      getAllThemesLoading,
      getAllThemesSuccess,

      themes,
      getTemplateInstancesLoading,
      getTemplateInstancesSuccess,
      templateInstances
    } = nextProps;
    const { themeId } = this.state;
    // After loading theme
    if (getAllThemesLoadingPrev && !getAllThemesLoading && getAllThemesSuccess) {
      // Update live theme
      const themeDetails = _.find(themes, { id: themeId }) || {};
      const isLiveTheme = themeDetails.role === 'main';
      this.setState({ isLiveTheme });
    }

    // After loading template instant
    if (
      getTemplateInstancesLoadingPrev &&
      !getTemplateInstancesLoading &&
      getTemplateInstancesSuccess
    ) {
      // Check current template
      const templateInstance = _.find(templateInstances, templateInstance =>
        templateInstance.themeIds?.includes(themeId)
      );

      if (templateInstance) {
        this.setState({ currentTemplateId: templateInstance.templateId });
      }
    }
  }

  componentWillUnmount() {
    this.props.cleanStateExitManageTemplatePage();
    this.cacheCurrentTheme();
    window.removeEventListener('beforeunload', this.cacheCurrentTheme);
  }

  cacheCurrentTheme = () => {
    // Save theme ID for manage template page
    try {
      const { themeId } = this.state;
      if (themeId) localStorage.setItem(`${getShop()}-manageTemplate`, themeId);
    } catch (error) {}
  };

  checkTheme = () => {
    let { selectedThemeId, themes, updateFieldReducer } = this.props;
    // const themeId = 126973313105;
    // const currentTemplateId = '40b33c6d-0c48-4755-9638-e4ab204806b0';
    // const isLiveTheme = false;
    let isLiveTheme = false;
    if (!selectedThemeId) {
      // Get data from local storage
      try {
        const selectedThemeIdLocalStorage = localStorage.getItem(`${getShop()}-manageTemplate`);
        selectedThemeId = selectedThemeIdLocalStorage
          ? parseInt(selectedThemeIdLocalStorage)
          : undefined;
      } catch (error) {}
    }

    if (selectedThemeId && themes) {
      const themeDetails = _.find(themes, { id: selectedThemeId }) || {};
      isLiveTheme = themeDetails.role === 'main';
    }
    this.setState({ themeId: selectedThemeId, isLiveTheme });
    updateFieldReducer('selectedThemeId', selectedThemeId);
  };

  handleLoadingPreview = () => {
    this.loadingBar && this.loadingBar.continuousStart();
  };

  handleCompleteLoadingPreview = () => {
    this.loadingBar && this.loadingBar.complete();
  };

  getSortedTemplates = () => {
    const {
      clonedTemplateInstance = {},
      templateInstances,
      duplicatedTemplateInstance
    } = this.props;
    const { currentTemplateId } = this.state;

    const sortedTemplateInstances = _.cloneDeep(templateInstances);

    // Move Just added template to top
    if (!_.isEmpty(clonedTemplateInstance) && !_.isEmpty(templateInstances)) {
      const [templateInstance] = _.remove(sortedTemplateInstances, {
        templateId: clonedTemplateInstance.templateId
      });

      if (templateInstance) sortedTemplateInstances.unshift(templateInstance);
    }

    // Move Just added template to top
    if (!_.isEmpty(duplicatedTemplateInstance) && !_.isEmpty(templateInstances)) {
      const [templateInstance] = _.remove(sortedTemplateInstances, {
        templateId: duplicatedTemplateInstance.templateId
      });

      if (templateInstance) sortedTemplateInstances.unshift(templateInstance);
    }

    // Move live template to top
    if (!_.isEmpty(templateInstances)) {
      const liveTemplate = _.find(templateInstances, template => {
        const isLive = this.checkLinkedWithLiveTheme(template);
        return isLive;
      });
      if (liveTemplate) {
        const [templateInstance] = _.remove(sortedTemplateInstances, {
          templateId: liveTemplate.templateId
        });

        if (templateInstance) sortedTemplateInstances.unshift(templateInstance);
      }
    }

    // Move current template instance to top of list
    if (!_.isEmpty(currentTemplateId) && !_.isEmpty(templateInstances)) {
      const [templateInstance] = _.remove(sortedTemplateInstances, {
        templateId: currentTemplateId
      });

      if (templateInstance) sortedTemplateInstances.unshift(templateInstance);
    }

    return sortedTemplateInstances;
  };

  renderTemplates = () => {
    const { i18n, getTemplateInstancesLoading } = this.props;

    if (getTemplateInstancesLoading) {
      return (
        <Card.Section>
          <div className="note">
            <SkeletonDisplayText size="small" />
          </div>
          <div className="list">
            <ul>
              {_.map([1, 2, 3, 4, 5], index => {
                return (
                  <li key={'skeleton-template-' + index}>
                    <SkeletonDisplayText size="small" />
                  </li>
                );
              })}
            </ul>
          </div>
        </Card.Section>
      );
    }

    const sortedTemplateInstances = this.getSortedTemplates();
    if (_.isEmpty(sortedTemplateInstances)) {
      return (
        <div className="empty-content">
          <EmptyState
            fullWidth
            heading={i18n.translate(
              'AdminHTML.ShopifyIntegration.ManageTemplateInstance.noTemplate_Title'
            )}
            action={{
              content: i18n.translate('AdminHTML.ShopifyIntegration.Actions.addNewTemplate'),
              onAction: this.handleChangeModal
            }}
            image={EmptyTemplate}>
            <p>
              {i18n.translate(
                'AdminHTML.ShopifyIntegration.ManageTemplateInstance.noTemplate_Description1'
              )}
            </p>
            <p>
              {i18n.translate(
                'AdminHTML.ShopifyIntegration.ManageTemplateInstance.noTemplate_Description2'
              )}
            </p>
          </EmptyState>
        </div>
      );
    }

    const note = (
      <TextStyle variation="strong">
        {sortedTemplateInstances.length}/{LIMIT_CUSTOM_TEMPLATE_INSTANCE}
      </TextStyle>
    );
    const { currentTemplateId } = this.state;

    return (
      <Card.Section>
        <div className="note">
          {i18n.translate('AdminHTML.ShopifyIntegration.ManageTemplateInstance.noteTotalTemplate', {
            t: note
          })}
        </div>
        <div className="list">
          <ul>
            {_.map(sortedTemplateInstances, templateInstance => {
              return this.renderItem(templateInstance, currentTemplateId);
            })}
          </ul>
        </div>
      </Card.Section>
    );
  };

  render() {
    const {
      i18n,
      themes = [],
      templateInstances = [],
      boostTemplates = [],

      getTemplateInstancesLoading,

      // Cloning from boost template instance
      cloneBoostTemplatesLoading = false,

      // Rename template
      renameTemplatesLoading = false,

      // Remove template
      removeTemplateLoading = false,

      // Duplicate template
      duplicateTemplateLoading = false,

      // Change template
      integratingTheme = false,

      isTemplateLibrary
    } = this.props;
    const {
      themeId,
      isLiveTheme,
      currentTemplateId,
      openModal,
      openRenameModal,
      selectedTemplateInstance,
      selectedTemplateName,
      openChangeModal,
      openRemoveModal,
      openCustomizeModal,
      openTemplateChangedModal
    } = this.state;
    console.log(
      `themeId: ${themeId}, liveTheme: ${isLiveTheme}, currentTemplateId: ${currentTemplateId}`
    );

    const sortedTemplateInstances = this.getSortedTemplates();
    const themeDetails = _.find(themes, { id: themeId });
    const themeName = _.get(themeDetails, 'name', '');

    return (
      <div className="BC-Page BC-Page__ShopifyIntegration BC-Page BC-Page__ShopifyIntegration__Manage">
        <LoadingBar color="rgba(0,126,98,1)" shadow={true} ref={e => (this.loadingBar = e)} />
        <MainMenu menuId="menu-manage-shopify-integration" />
        <Page
          title={i18n.translate('AdminHTML.ShopifyIntegration.ManageTemplateInstance.pageTitle')}
          primaryAction={
            !getTemplateInstancesLoading && !_.isEmpty(templateInstances)
              ? {
                  content: i18n.translate('AdminHTML.ShopifyIntegration.Actions.addNewTemplate'),
                  disabled:
                    sortedTemplateInstances.length >= LIMIT_CUSTOM_TEMPLATE_INSTANCE ||
                    cloneBoostTemplatesLoading ||
                    duplicateTemplateLoading ||
                    getTemplateInstancesLoading,
                  onAction: () => this.handleChangeModal()
                }
              : null
          }
          breadcrumbs={
            isTemplateLibrary
              ? []
              : [
                  {
                    content: '',
                    onAction: this.handleGoBack
                  }
                ]
          }>
          {isTemplateLibrary ? (
            <TitleHelp i18n={i18n} hrefUserManual={URL['ManageTemplateLibrary']} />
          ) : (
            <div id="manage-template-theme-name">{themeName}</div>
          )}

          <div className="management-template">
            <Layout>
              <Layout.Section>
                <Card>{this.renderTemplates()}</Card>
              </Layout.Section>
            </Layout>
          </div>

          <SelectTemplateModal
            open={openModal}
            disabling={cloneBoostTemplatesLoading}
            secondaryAction={'cancel'}
            onClose={this.handleChangeModal}
            onConfirm={this.handleCloneConfirmModal}
            integratedTemplateInstance={{}}
            boostTemplates={boostTemplates}
            templateInstances={[]}
          />

          <RenameTemplateModal
            open={openRenameModal}
            loading={renameTemplatesLoading}
            onClose={this.handleChangeRenameModal}
            onChange={this.handleEditName}
            onConfirm={this.handleConfirmRenameModal}
            templateName={selectedTemplateName}
          />

          <ChangeTemplateModal
            open={openChangeModal}
            loading={integratingTheme}
            onClose={this.handleChangeTemplateModal}
            onConfirm={this.handleConfirmChangeModal}
            templateName={selectedTemplateName}
          />

          <RemoveTemplateModal
            open={openRemoveModal}
            loading={removeTemplateLoading}
            onClose={this.handleRemoveTemplateModal}
            onConfirm={this.handleConfirmRemoveModal}
            templateInstance={selectedTemplateInstance}
            themes={themes}
            linkedWithLiveTheme={this.checkLinkedWithLiveTheme(selectedTemplateInstance)}
          />

          <CustomizeTemplateModal
            open={openCustomizeModal}
            loading={duplicateTemplateLoading || integratingTheme}
            onClose={this.handleCustomizeTemplateModal}
            onConfirm={this.handleConfirmCustomizeModal}
            templateInstance={selectedTemplateInstance}
            isReachLimitCustomTemplate={templateInstances.length >= LIMIT_CUSTOM_TEMPLATE_INSTANCE}
            prevRoute={
              isTemplateLibrary
                ? routes.shopifyIntegration.templateLibrary()
                : routes.shopifyIntegration.manageTemplateInstance()
            }
          />

          <TemplateChangedModal
            open={openTemplateChangedModal}
            onClose={this.handleHideTemplateChangedModal}
            integratedTemplateInstance={selectedTemplateInstance}
            selectedTheme={themeDetails}
          />
        </Page>
      </div>
    );
  }

  renderItem = (templateInstance, currentTemplateId) => {
    const {
      i18n,
      templateInstances = {},
      clonedTemplateInstance = {},
      duplicatedTemplateInstance = {},
      integratingTheme,
      duplicateTemplateLoading,
      isTemplateLibrary,
      themes
    } = this.props;
    const { templateId, templateName, themeNameLib, createdAt, updatedAt, templateVersion } =
      templateInstance;

    const thumbnails = {
      default: ThumbnailDefaultTemplate,
      dawn: ThumbnailBasicTemplate
    };

    const boostTemplate = _.find(templateInstances, { themeNameLib });
    const boostTemplateVersion = _.get(boostTemplate, 'templateVersion', '1.0.0');
    const linkedWithLiveTheme = this.checkLinkedWithLiveTheme(templateInstance);

    const currentTemplateBadge =
      currentTemplateId === templateInstance.templateId ? (
        <Badge status="success">
          {i18n.translate('AdminHTML.ShopifyIntegration.SelectTemplate.badgeCurrentTemplate')}
        </Badge>
      ) : clonedTemplateInstance.templateId === templateInstance.templateId ||
        duplicatedTemplateInstance.templateId === templateInstance.templateId ? (
        <Badge status="info">
          {i18n.translate('AdminHTML.ShopifyIntegration.SelectTemplate.badgeJustAdded')}
        </Badge>
      ) : (
        ''
      );

    const linkedLiveThemeBadge = linkedWithLiveTheme ? (
      <Badge status="success">
        {i18n.translate('AdminHTML.ShopifyIntegration.SelectTemplate.usedInLiveTheme')}
      </Badge>
    ) : null;

    let lastSavedAt = updatedAt || createdAt;
    lastSavedAt = moment(lastSavedAt * 1000).format('MMM DD, YYYY [at] h:mm A');

    return (
      <li key={templateId}>
        <div className="item">
          <img className="thumbnail" src={thumbnails[themeNameLib]} width={90} height={72} alt="" />
          <div className="details">
            <div className="title">
              <span>{templateName}</span> {currentTemplateBadge} {linkedLiveThemeBadge}
            </div>
            <div className="description">
              <span>
                {i18n.translate(
                  'AdminHTML.ShopifyIntegration.ManageTemplateInstance.description_TemplateId'
                )}
                : {templateId}
              </span>
              <Button icon={DuplicateMinor} plain onClick={() => copyClipBoard(i18n, templateId)} />
            </div>
            <div className="description">
              <span>
                {i18n.translate(
                  'AdminHTML.ShopifyIntegration.ManageTemplateInstance.description_Version',
                  {
                    current: templateVersion,
                    latest: boostTemplateVersion
                  }
                )}
              </span>
            </div>
            <div className="description">
              <span>
                {i18n.translate('AdminHTML.ShopifyIntegration.SelectTemplateModal.lastSaved')}{' '}
                {lastSavedAt}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="action-page" style={{ flexShrink: 0 }}>
            <ButtonGroup spacing="tight">
              {/* Change, Manage template */}
              <PopOverActionManageButton
                isTemplateLibrary={isTemplateLibrary}
                onPreview={this.handlePreview}
                onDuplicateTemplate={() => this.handleDuplicateTemplate(templateInstance)}
                onEditNameTemplate={() => this.handleEditNameTemplate(templateInstance)}
                onRemoveTemplate={() => this.handleRemoveTemplate(templateInstance)}
                onChangeTemplate={
                  isTemplateLibrary ? null : () => this.handleChangeTemplate(templateInstance)
                }
                isReachLimitCustomTemplate={
                  templateInstances.length >= LIMIT_CUSTOM_TEMPLATE_INSTANCE
                }
                templateId={templateId}
                currentTemplateId={currentTemplateId}
                templateInstance={templateInstance}
                themes={themes}
                disabled={integratingTheme || duplicateTemplateLoading}
              />
              <div className="btn-customize">
                <Button onClick={() => this.handleCustomizeTemplate(templateInstance)}>
                  {i18n.translate('AdminHTML.ShopifyIntegration.Actions.customize')}
                </Button>
              </div>
            </ButtonGroup>
          </div>
        </div>
      </li>
    );
  };

  checkLinkedWithLiveTheme = templateInstance => {
    const { themes = [] } = this.props;
    const themeIds = _.get(templateInstance, 'themeIds', []);

    // console.log(`checkLinkedWithLiveTheme`, isLiveTheme, templateInstance);
    // console.log(`checkLinkedWithLiveTheme`, themes);

    let linkedWithLiveTheme = false;
    for (const themeId of themeIds) {
      if (linkedWithLiveTheme) break;

      const themeDetails = _.find(themes, { id: themeId });
      if (!_.isEmpty(themeDetails) && themeDetails.role === 'main') {
        linkedWithLiveTheme = true;
      }
    }

    return linkedWithLiveTheme;
  };

  handleChangeModal = () => {
    const { openModal } = this.state;
    this.setState({ openModal: !openModal });
  };

  handleCloneConfirmModal = async (boostTemplateId, themeNameLib) => {
    const { i18n, cloneBoostTemplateInstances, addCreatedTemplateInstance } = this.props;

    await cloneBoostTemplateInstances(themeNameLib).then(async response => {
      const { type = '' } = response;
      if (type === 'SI_CLONE_BOOST_TEMPLATES_SUCCESS') {
        const clonedTemplateInstance = this.props.clonedTemplateInstance;

        if (!_.isEmpty(clonedTemplateInstance)) {
          addCreatedTemplateInstance(clonedTemplateInstance, true);
        }

        // Show toast message
        showSABToast(
          i18n.translate('AdminHTML.ShopifyIntegration.SelectTemplateModal.addNewTemplateSuccess'),
          false
        );

        // Change state
        this.setState({ selectedTemplateId: '', selectedTemplateName: '' });

        // Close modal
        this.setState({ openModal: false });
      }
    });
  };

  handleGoBack = () => {
    const { themeId } = this.state;
    const { updateFieldReducer, isTemplateLibrary, isIsNewTemplate } = this.props;

    if (isTemplateLibrary) {
      history.push({
        pathname: routes.shopifyIntegration.view()
      });
      return;
    }

    if (isIsNewTemplate) {
      updateFieldReducer('isIsNewTemplate', false);
      history.push({
        pathname: routes.shopifyIntegration.newThemeWidget(themeId)
      });
    } else {
      history.push({
        pathname: routes.shopifyIntegration.viewThemeWidget(themeId)
      });
    }
  };

  handlePreview = () => {
    const { themes } = this.props;
    const { themeId } = this.state;
    const themeDetails = _.find(themes, { id: themeId });

    const previewUrl = getThemeReviewUrl(themeId);

    // Track Full Story event
    trackFSEvent('Preview theme', {
      feature: 'SSR Theme Setup',
      themeName: themeDetails?.name
    });

    window.open(previewUrl, '_blank');
  };

  handleDuplicateTemplate = async templateInstance => {
    const { templateId, templateName } = templateInstance;
    const { i18n, duplicateTemplateInstance, themes } = this.props;
    const { themeId } = this.state;

    if (!templateId) {
      return;
    }

    this.handleLoadingPreview();
    duplicateTemplateInstance(templateId)
      .then(response => {
        const { type = '' } = response;
        if (type === 'SI_DUPLICATE_TEMPLATE_SUCCESS') {
          // Track Full Story event
          const themeDetails = _.find(themes, { id: themeId });
          trackFSEvent('Duplicate a Template', {
            feature: 'SSR Theme Setup',
            selectedTemplate: templateName,
            templateId,
            themeName: themeDetails?.name
          });

          showSABToast(
            i18n.translate(
              'AdminHTML.ShopifyIntegration.ManageTemplateInstance.duplicateTemplateSuccess'
            ),
            false
          );
        }
      })
      .finally(() => {
        this.handleCompleteLoadingPreview();
      });
  };

  /** Rename template */
  handleChangeRenameModal = () => {
    const { openRenameModal } = this.state;
    const state = { openRenameModal: !openRenameModal };

    // Change from open to close
    if (openRenameModal) {
      _.assign(state, { selectedTemplateId: '', selectedTemplateName: '' });
    }

    this.setState(state);
  };

  handleEditNameTemplate = templateInstance => {
    const { templateId, templateName } = templateInstance;
    this.setState({
      selectedTemplateId: templateId,
      selectedTemplateName: templateName,
      openRenameModal: true
    });
  };

  handleEditName = templateName => {
    this.setState({ selectedTemplateName: templateName });
  };

  handleConfirmRenameModal = async templateName => {
    const { selectedTemplateId, themeId } = this.state;
    const { i18n, renameTemplateInstance, themes } = this.props;

    if (!selectedTemplateId || !templateName) {
      return;
    }

    await renameTemplateInstance(selectedTemplateId, templateName.trim()).then(async response => {
      const { type = '' } = response;
      if (type === 'SI_RENAME_TEMPLATES_SUCCESS') {
        const themeDetails = _.find(themes, { id: themeId });
        trackFSEvent('Rename A Template', {
          feature: 'SSR Theme Setup',
          themeName: themeDetails?.name,
          templateName: templateName.trim(),
          templateId: selectedTemplateId
        });
        // Show toast message
        showSABToast(
          i18n.translate('AdminHTML.ShopifyIntegration.RenameModal.renameTemplateSuccess'),
          false
        );
      }

      // Close modal
      this.handleChangeRenameModal();
    });
  };
  /** End Rename template */

  /** Change template */
  handleChangeTemplateModal = () => {
    const { openChangeModal } = this.state;
    this.setState({ openChangeModal: !openChangeModal });
  };

  /** Changed template modal */
  handleHideTemplateChangedModal = () => {
    this.setState({ openTemplateChangedModal: false });
  };

  handleChangeTemplate = templateInstance => {
    const { templateId, templateName } = templateInstance;
    this.setState({
      selectedTemplateId: templateId,
      selectedTemplateName: templateName,
      openChangeModal: true
    });
  };

  handleConfirmChangeModal = async () => {
    const { themeId = '', selectedTemplateId = '' } = this.state;
    const { i18n, themeIntegrationSetup, templateInstances } = this.props;
    if (!selectedTemplateId || !themeId) {
      return;
    }
    const templateInstance = _.find(templateInstances, { templateId: selectedTemplateId });
    if (templateInstance) {
      const { themeNameLib } = templateInstance;
      await themeIntegrationSetup(themeId, selectedTemplateId, themeNameLib, false).then(
        async response => {
          const { type = '' } = response;
          if (type === 'SI_THEME_INTEGRATION_SETUP_SUCCESS') {
            const { themes, createdTemplateInstance = {}, updateThemeIdsOfTemplate } = this.props;
            const { currentTemplateId } = this.state;
            updateThemeIdsOfTemplate(currentTemplateId, selectedTemplateId, themeId);

            // Change selected templateId
            const { templateId, templateName } = createdTemplateInstance;
            // Track Full Story event
            const themeDetails = _.find(themes, { id: themeId });
            if (currentTemplateId) {
              trackFSEvent('Change a Template', {
                feature: 'SSR Theme Setup',
                themeName: themeDetails?.name,
                templateName: templateName,
                templateId: templateId,
                oldTemplateId: currentTemplateId
              });
            } else {
              trackFSEvent('Select A Template', {
                feature: 'SSR Theme Setup',
                themeName: themeDetails?.name,
                templateName: templateName,
                templateId: templateId
              });
            }
            this.setState({
              currentTemplateId: templateId,
              selectedTemplateInstance: createdTemplateInstance,
              selectedTemplateName: _.get(createdTemplateInstance, 'templateName'),
              openChangeModal: false,
              openTemplateChangedModal: true
            });
            // Show toast message
            showSABToast(
              i18n.translate(
                'AdminHTML.ShopifyIntegration.ChangeTemplateModal.changeTemplateSuccess'
              ),
              false
            );
          }
        }
      );
    }
  };
  /** End Change template */

  /** Remove template */
  handleRemoveTemplateModal = () => {
    const { openRemoveModal } = this.state;
    this.setState({ openRemoveModal: !openRemoveModal });
  };

  handleRemoveTemplate = templateInstance => {
    const { templateId, templateName } = templateInstance;
    this.setState({
      selectedTemplateInstance: templateInstance,
      selectedTemplateId: templateId,
      selectedTemplateName: templateName,
      openRemoveModal: true
    });
  };

  handleConfirmRemoveModal = async () => {
    const { selectedTemplateInstance = {}, themeId } = this.state;
    const { templateId, templateName } = selectedTemplateInstance;
    const { i18n, removeTemplateInstance, themes } = this.props;

    if (!templateId) {
      return;
    }

    await removeTemplateInstance(templateId, false).then(async response => {
      const { type = '' } = response;
      if (type === 'SI_REMOVE_TEMPLATE_SUCCESS') {
        // Track Full Story event
        const themeDetails = _.find(themes, { id: themeId });
        trackFSEvent('Remove a Template', {
          feature: 'SSR Theme Setup',
          selectedTemplate: templateName,
          templateId,
          themeName: themeDetails?.name
        });

        // Show toast message
        showSABToast(
          i18n.translate('AdminHTML.ShopifyIntegration.RemoveTemplateModal.removeTemplateSuccess'),
          false
        );
      }

      // Close modal
      this.handleRemoveTemplateModal();
    });
  };
  /** End Remove template */

  /** Customize template */
  handleCustomizeTemplateModal = () => {
    const { openCustomizeModal } = this.state;
    this.setState({ openCustomizeModal: !openCustomizeModal });
  };

  handleCustomizeTemplate = templateInstance => {
    const { templateId, templateName } = templateInstance;
    const { isLiveTheme = false, themeId } = this.state;
    const { themes, isTemplateLibrary } = this.props;
    const state = {
      selectedTemplateInstance: templateInstance,
      selectedTemplateId: templateId,
      selectedTemplateName: templateName
    };

    // Check link with live theme
    const linkedWithLiveTheme = this.checkLinkedWithLiveTheme(templateInstance);

    // Track Full Story event
    const themeDetails = _.find(themes, { id: themeId });
    trackFSEvent('Customize a Template', {
      feature: 'SSR Theme Setup',
      selectedTemplate: templateName,
      templateId,
      themeName: themeDetails?.name
    });

    // Show warning modal: Unpublished theme and Template linked with live theme
    if (!isLiveTheme && linkedWithLiveTheme) {
      state.openCustomizeModal = true;
      this.setState(state);
    } else {
      this.setState(state);
      savePrevRoute(
        isTemplateLibrary
          ? routes.shopifyIntegration.templateLibrary()
          : routes.shopifyIntegration.manageTemplateInstance()
      );
      history.push({ pathname: routes.shopifyIntegrationSettings.visualEditor(templateId) });
      return;
    }
  };

  handleConfirmCustomizeModal = () => {
    const { selectedTemplateInstance = {} } = this.state;
    const { templateId } = selectedTemplateInstance;
    const {
      i18n,
      duplicateTemplateInstance,
      themeIntegrationSetup,
      selectedThemeId,
      updateFieldReducer,
      isTemplateLibrary,
      templateInstances,
      updateThemeIdsOfTemplate
    } = this.props;

    if (!templateId) {
      return;
    }

    duplicateTemplateInstance(templateId).then(responseDuplicate => {
      const { type = '' } = responseDuplicate;
      const duplicateTemplateId = _.get(responseDuplicate, 'payload.data.templateId', '');
      const themeNameLib = _.get(responseDuplicate, 'payload.data.themeNameLib', '');

      const currentTemplateInstance = _.find(templateInstances, templateInstance =>
        templateInstance.themeIds?.includes(this.state.themeId)
      );
      const isLinkedLiveTheme = this.checkLinkedWithLiveTheme(currentTemplateInstance);

      // template library page or current template is not published theme and linked with live theme -> not auto link
      if (isTemplateLibrary || !isLinkedLiveTheme) {
        this.setState({
          openCustomizeModal: false
        });
        return;
      }

      if (type === 'SI_DUPLICATE_TEMPLATE_SUCCESS') {
        // Call setup and then add new created template instance to state
        themeIntegrationSetup(selectedThemeId, duplicateTemplateId, themeNameLib, false)
          .then(response => {
            const { type = '' } = response;
            const { currentTemplateId } = this.state;

            if (type === 'SI_THEME_INTEGRATION_SETUP_SUCCESS') {
              // const createdTemplateInstance = this.props.createdTemplateInstance;

              updateFieldReducer('currentTemplateId', duplicateTemplateId);
              updateThemeIdsOfTemplate(currentTemplateId, duplicateTemplateId, selectedThemeId);

              // Save state
              this.setState({
                currentTemplateId: duplicateTemplateId,
                selectedTemplateInstance: _.get(response, 'payload.data'),
                selectedTemplateName: _.get(response, 'payload.data.templateName'),
                openCustomizeModal: false
              });

              setTimeout(() => {
                this.setState({
                  openTemplateChangedModal: true
                });
              }, 300);

              showSABToast(
                i18n.translate(
                  'AdminHTML.ShopifyIntegration.ManageTemplateInstance.duplicateTemplateSuccess'
                ),
                false
              );
            }
          })
          .catch(error => {
            console.log('ERROR', error);
          });
      }
    });
  };
  /** End Customize template */
}

const mapStateToProps = (state, props) => {
  const { shopifyIntegrationTheme = {}, templateInstance = {}, themeIntegration = {} } = state;

  const themes = _.get(shopifyIntegrationTheme, 'allThemes', []);
  const getAllThemesLoading = _.get(shopifyIntegrationTheme, 'getAllThemesLoading', false);
  const getAllThemesSuccess = _.get(shopifyIntegrationTheme, 'getAllThemesSuccess', false);

  const boostTemplates = _.get(templateInstance, 'boostTemplates', []);
  const templateInstances = _.get(templateInstance, 'templateInstances', []);
  const getTemplateInstancesLoading = _.get(templateInstance, 'getTemplateInstancesLoading', false);
  const getTemplateInstancesSuccess = _.get(templateInstance, 'getTemplateInstancesSuccess', false);

  const clonedTemplateInstance = _.get(templateInstance, 'clonedTemplateInstance', {});
  const cloneBoostTemplatesLoading = _.get(templateInstance, 'cloneBoostTemplatesLoading', false);

  const renameTemplatesLoading = _.get(templateInstance, 'renameTemplatesLoading', false);
  const renamedTemplateInstance = _.get(templateInstance, 'renamedTemplateInstance', {});

  const removeTemplateLoading = _.get(templateInstance, 'removeTemplateLoading', false);

  const duplicateTemplateLoading = _.get(templateInstance, 'duplicateTemplateLoading', false);
  const duplicatedTemplateInstance = _.get(templateInstance, 'duplicatedTemplateInstance', {});

  // Theme integration setup
  const integratingTheme = _.get(themeIntegration, 'integratingTheme', false);
  const createdTemplateInstance = _.get(themeIntegration, 'createdTemplateInstance', {});

  return {
    // Shopify themes
    themes,
    getAllThemesLoading,
    getAllThemesSuccess,
    selectedThemeId: _.get(themeIntegration, 'selectedThemeId', ''),
    currentTemplateId: _.get(themeIntegration, 'currentTemplateId', ''),
    isLiveTheme: _.get(themeIntegration, 'isLiveTheme', false),
    isIsNewTemplate: _.get(themeIntegration, 'isIsNewTemplate', false),

    // Boost templates
    boostTemplates,

    // Store template instances
    getTemplateInstancesLoading,
    getTemplateInstancesSuccess,
    templateInstances,

    // Clone template instance
    clonedTemplateInstance,
    cloneBoostTemplatesLoading,

    // Rename template instance
    renameTemplatesLoading,
    renamedTemplateInstance,

    // Remove template instance
    removeTemplateLoading,

    // Duplicate template instance
    duplicateTemplateLoading,
    duplicatedTemplateInstance,

    // Change template instance
    integratingTheme,
    createdTemplateInstance
  };
};
const mapDispatchToProps = (dispatch, ownProps) => ({
  getAllThemes: () => dispatch(shopifyIntegrationThemeAction.getAllThemes(ownProps.i18n)),
  getBoostTemplateInstances: () =>
    dispatch(templateInstanceAction.getBoostTemplateInstances(ownProps.i18n)),
  getTemplateInstances: () => dispatch(templateInstanceAction.getTemplateInstances(ownProps.i18n)),
  cloneBoostTemplateInstances: themeNameLib =>
    dispatch(templateInstanceAction.cloneBoostTemplateInstances(ownProps.i18n, themeNameLib)),
  addCreatedTemplateInstance: (templateInstance, isBoostTemplate) =>
    dispatch(templateInstanceAction.addCreatedTemplateInstance(templateInstance, isBoostTemplate)),
  renameTemplateInstance: (templateId, templateName) =>
    dispatch(
      templateInstanceAction.renameTemplateInstance(ownProps.i18n, templateId, templateName)
    ),
  removeTemplateInstance: templateId =>
    dispatch(templateInstanceAction.removeTemplateInstance(ownProps.i18n, templateId)),
  duplicateTemplateInstance: templateId =>
    dispatch(templateInstanceAction.duplicateTemplateInstance(ownProps.i18n, templateId)),
  themeIntegrationSetup: (themeId, templateId, themeNameLib, isBoostTemplate) =>
    dispatch(
      themeIntegrationAction.themeIntegrationSetup(
        ownProps.i18n,
        themeId,
        templateId,
        themeNameLib,
        isBoostTemplate
      )
    ),
  cleanStateExitManageTemplatePage: () =>
    dispatch(templateInstanceAction.cleanStateExitManageTemplatePage()),
  updateFieldReducer: (fieldName, data) =>
    dispatch(themeIntegrationAction.handleUpdateField(fieldName, data)),
  updateThemeIdsOfTemplate: (fromTemplateId, toTemplateId, themeId) =>
    dispatch(
      templateInstanceAction.handleUpdateThemeIdsOfTemplate(fromTemplateId, toTemplateId, themeId)
    )
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(ManageTemplateInstance));
